// adminService.js
import apiClient from '../utils/apiClient';
import { auth } from '../firebase';
import { confirmPasswordReset } from 'firebase/auth';
import { handleApiError } from '../utils/apiService';
import { schoolsApi } from '../utils/apiService';
import config from '../config';

const API_URL = config.apiUrl;

// Cache configuration
const cache = new Map();
const CACHE_DURATION = 3600000; // 1 hour

// Local storage backup cache for persistence between page refreshes
const getFromLocalStorage = (key) => {
  try {
    const stored = localStorage.getItem(`edc_cache_${key}`);
    if (stored) {
      const { data, timestamp } = JSON.parse(stored);
      if (Date.now() - timestamp < CACHE_DURATION) {
        return { data, timestamp };
      }
    }
    return null;
  } catch (e) {
    console.warn('LocalStorage cache error:', e);
    return null;
  }
};

const saveToLocalStorage = (key, data) => {
  try {
    localStorage.setItem(`edc_cache_${key}`, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.warn('LocalStorage save error:', e);
  }
};

const withCache = async (key, fetchFn) => {
  const now = Date.now();
  
  // Check in-memory cache first (fastest)
  const cached = cache.get(key);
  if (cached && now - cached.timestamp < CACHE_DURATION) {
    return cached.data;
  }
  
  // Then check localStorage (persists between page refreshes)
  const storedCache = getFromLocalStorage(key);
  if (storedCache) {
    // Update in-memory cache with localStorage data
    cache.set(key, storedCache);
    return storedCache.data;
  }

  // If no cache hit, fetch fresh data
  try {
    const data = await fetchFn();
    
    // Update both caches
    const cacheEntry = {
      data,
      timestamp: now
    };
    
    cache.set(key, cacheEntry);
    saveToLocalStorage(key, data);
    
    return data;
  } catch (error) {
    // If fetch fails but we have stale cache, return it with a console warning
    const staleCache = cache.get(key);
    if (staleCache) {
      console.warn('Using stale cache due to fetch error:', error);
      return staleCache.data;
    }
    throw error;
  }
};

const clearCache = (pattern) => {
  // Clear in-memory cache
  if (pattern) {
    for (const [key] of cache.entries()) {
      if (pattern instanceof RegExp ? pattern.test(key) : key.includes(pattern)) {
        cache.delete(key);
        // Also clear from localStorage
        try {
          localStorage.removeItem(`edc_cache_${key}`);
        } catch (e) {
          console.warn('LocalStorage clear error:', e);
        }
      }
    }
  } else {
    cache.clear();
    // Clear all cache entries from localStorage
    try {
      Object.keys(localStorage)
        .filter(key => key.startsWith('edc_cache_'))
        .forEach(key => localStorage.removeItem(key));
    } catch (e) {
      console.warn('LocalStorage clear all error:', e);
    }
  }
};

const adminService = {
  // User Management
  /** Fast minimal list (Auth-only): for staggered loading, names first. No cache. */
  listUsersMinimal: async (enqueueSnackbar) => {
    try {
      const response = await apiClient.get('/v2/admin/users', {
        params: { minimal: 1 }
      });
      if (!response) throw new Error('No response from server');
      return response;
    } catch (error) {
      console.error('List users (minimal) error:', { error, status: error.status, message: error.message });
      if (enqueueSnackbar) handleApiError(error, enqueueSnackbar);
      throw error;
    }
  },

  /** Full list (Firestore + SQL): for background enrich after minimal. No cache. */
  listUsersFull: async (enqueueSnackbar) => {
    try {
      const response = await apiClient.get('/v2/admin/users');
      if (!response) throw new Error('No response from server');
      return response;
    } catch (error) {
      console.error('List users (full) error:', { error, status: error.status, message: error.message });
      if (enqueueSnackbar) handleApiError(error, enqueueSnackbar);
      throw error;
    }
  },

  listUsers: (page = 0, pageSize = 25, enqueueSnackbar) => withCache(`users-${page}-${pageSize}`, async () => {
    try {
      // Use the new v2 route that includes team information
      const response = await apiClient.get('/v2/admin/users', {
        params: {
          page,
          pageSize
        }
      });
      
      if (!response) {
        throw new Error('No response from server');
      }
      
      return response;
    } catch (error) {
      console.error('List users error:', {
        error,
        status: error.status,
        message: error.message
      });
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  }),
  
  createUser: async (userData, enqueueSnackbar) => {
    try {
      const response = await apiClient.post('/v2/admin/users', userData);
      clearCache('users');
      return response;
    } catch (error) {
      console.error('Create user error:', error);
      
      if (error.status === 409 || 
          error.data?.error === 'EMAIL_EXISTS' || 
          error.message?.includes('already exists')) {
        error.message = `User with email ${userData.email} already exists`;
      }
      
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  updateUser: async (uid, userData, enqueueSnackbar) => {
    try {
      const response = await apiClient.put(`/admin/users/${uid}`, userData);
      clearCache('users');
      return response;
    } catch (error) {
      console.error('Update user error:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  bulkCreateUsers: async (usersData, enqueueSnackbar) => {
    try {
      const response = await apiClient.post('/admin/users/bulk', { users: usersData });
      clearCache('users');
      return response;
    } catch (error) {
      console.error('Bulk create users error:', error);
      if (error.status === 409) {
        error.message = 'One or more users already exist';
      } else if (error.status === 400) {
        error.message = 'Invalid user data in upload';
      }
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  suspendUser: async (uid, enqueueSnackbar) => {
    try {
      const response = await apiClient.post(`/admin/users/${uid}/suspend`);
      clearCache('users');
      return response;
    } catch (error) {
      console.error('Error message:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  reactivateUser: async (uid, enqueueSnackbar) => {
    try {
      const response = await apiClient.post(`/admin/users/${uid}/reactivate`);
      clearCache('users');
      return response;
    } catch (error) {
      console.error('Error message:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  deleteUser: async (uid, confirmationEmail, enqueueSnackbar) => {
    try {
      const response = await apiClient.delete(`/admin/users/${uid}/delete`, {
        data: { confirmationEmail }
      });
      clearCache('users');
      return response;
    } catch (error) {
      console.error('Error message:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  sendUserInvite: async (uid, enqueueSnackbar) => {
    try {
      const response = await apiClient.post(`/admin/users/${uid}/invite`, {}, {
        timeout: 30000
      });

      if (response.status === 429) {
        const error = new Error(response.message || 'Rate limit exceeded');
        error.status = 429;
        error.json = async () => response;
        throw error;
      }

      clearCache('users');
      return response;
    } catch (error) {
      console.error('Error message:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  testEmail: async (enqueueSnackbar) => {
    try {
      return await apiClient.post('/admin/test-email');
    } catch (error) {
      console.error('Error message:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  getUserDetails: async (uid, enqueueSnackbar) => {
    try {
      return await apiClient.get(`/admin/users/${uid}/details`);
    } catch (error) {
      console.error('Error message:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  // Team Management (Database-based using organizational models)
  listTeams: (page = 0, pageSize = 25, enqueueSnackbar, getAllTeams = false) => withCache(`teams-${page}-${pageSize}-${getAllTeams}`, async () => {
    try {
      const response = await apiClient.get('/v2/admin/teams', {
        params: {
          page,
          pageSize,
          getAllTeams
        }
      });
      
      // Ensure consistent response format - always return the full response object
      // Frontend components should extract teams from response.teams
      return response;
    } catch (error) {
      console.error('List teams error:', error);
      if (error.status === 403) {
        error.message = 'You do not have permission to view teams';
      }
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  }),

  // DEPRECATED: Use listTeams instead - team summaries are included in the main teams list
  listTeamSummaries: async (enqueueSnackbar) => {
    console.warn('DEPRECATED: listTeamSummaries is deprecated. Use listTeams instead.');
    const response = await adminService.listTeams(0, 1000, enqueueSnackbar, true);
    // Return teams array for backward compatibility
    return response?.teams || [];
  },


  getTeamUsers: async (teamId, enqueueSnackbar) => {
    try {
      const response = await apiClient.get(`/v2/admin/teams/${teamId}/users`);
      return response?.users || [];
    } catch (error) {
      console.error('Get team users error:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  createTeam: async (teamData, enqueueSnackbar) => {
    try {
      const response = await apiClient.post('/v2/admin/teams', teamData);
      // Clear all teams cache entries to ensure fresh data is loaded
      clearCache('teams');
      return response;
    } catch (error) {
      console.error('Create team error:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  // DEPRECATED: Validation is now handled within the bulk create teams endpoint
  validateSchoolsForTeams: async (teamsData, enqueueSnackbar) => {
    console.warn('DEPRECATED: validateSchoolsForTeams is deprecated. Validation is now built into bulkCreateTeams.');
    // Call bulk create teams with validation-only mode
    return await adminService.bulkCreateTeams(teamsData, false, enqueueSnackbar);
  },

  bulkCreateTeams: async (teamsData, forceProceed = false, enqueueSnackbar) => {
    try {
      const response = await apiClient.post('/v2/admin/teams/bulk', { 
        teams: teamsData,
        force_proceed: forceProceed 
      });
      
      clearCache('teams');
      return response;
    } catch (error) {
      console.error('Error in bulk team creation:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  updateTeam: async (teamId, teamData, enqueueSnackbar) => {
    try {
      const response = await apiClient.put(`/v2/admin/teams/${teamId}`, teamData);
      clearCache('teams');
      return response;
    } catch (error) {
      console.error('Error message:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  deleteTeam: async (teamId, enqueueSnackbar) => {
    try {
      const response = await apiClient.delete(`/v2/admin/teams/${teamId}`);
      clearCache('teams');
      return response;
    } catch (error) {
      console.error('Error message:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  addSchoolsToTeam: async (teamId, schools, enqueueSnackbar) => {
    if (!teamId || !schools || !schools.length) {
      throw new Error('Invalid parameters');
    }
    try {
      const response = await apiClient.post(`/admin/teams/${teamId}/schools`, {
        schoolIds: schools
      });
      clearCache('teams');
      return response;
    } catch (error) {
      console.error('Add schools error:', error);
      if (error.status === 404) {
        error.message = 'Team or schools not found';
      } else if (error.status === 409) {
        error.message = 'One or more schools are already in this team';
      }
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  removeSchoolFromTeam: async (teamId, schoolId, enqueueSnackbar) => {
    try {
      const response = await apiClient.delete(`/v2/admin/teams/${teamId}/schools/${schoolId}`);
      clearCache('teams');
      return response;
    } catch (error) {
      console.error('Error message:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  // School Management
  searchSchools: async (filters, enqueueSnackbar) => {
    try {
        // Use v2 NCES school search endpoint
        const params = new URLSearchParams();
        
        if (filters.state) params.append('state', filters.state);
        if (filters.searchQuery) params.append('q', filters.searchQuery); // Changed from searchQuery to q
        if (filters.limit) params.append('limit', filters.limit);
        
        const response = await apiClient.get(`/v2/nces/schools/search?${params}`);
        
        // apiClient returns data directly, not wrapped in response.data
        
        // Check if response has schools array
        if (!response.schools || !Array.isArray(response.schools)) {
            console.error('No schools array in response:', response);
            throw new Error('Invalid response from server - missing schools array');
        }
        
        // Transform response to match expected format
        const schools = response.schools || [];
        // Transform the schools data to match expected format
        const transformedSchools = schools.map(school => ({
            id: school.uuid, // Map uuid to id for backward compatibility
            uuid: school.uuid,
            ncessch: school.ncessch,
            name: school.name,
            display_name: school.display_name, // From school_names table
            city: school.city || school.district || 'N/A', // Use city if available, fallback to district
            state: school.state
        }));
        

        
        // Return in the expected format for the frontend
        return {
            data: {
                schools: transformedSchools,
                hasMore: schools.length >= (filters.limit || 50)
            }
        };
    } catch (error) {
      console.error('Search schools error:', error);
      if (error.status === 404) {
        error.message = 'No schools found matching the search criteria';
      } else if (error.status === 400) {
        error.message = 'Invalid search parameters';
      }
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  // Team Groups (Database-based using organizational models)
  listTeamGroups: () => withCache('teamGroups', async (enqueueSnackbar) => {
    try {
      const response = await apiClient.get('/v2/admin/team-groups');
      if (!response) {
        throw new Error('No response from server');
      }
      return response;
    } catch (error) {
      console.error('Error message:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  }),

  createTeamGroup: async (groupData, enqueueSnackbar) => {
    try {
      const response = await apiClient.post('/v2/admin/team-groups', groupData);
      clearCache('teamGroups');
      return response;
    } catch (error) {
      console.error('Error message:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  updateTeamGroup: async (groupId, groupData, enqueueSnackbar) => {
    try {
      const response = await apiClient.put(`/v2/admin/team-groups/${groupId}`, groupData);
      clearCache('teamGroups');
      return response;
    } catch (error) {
      console.error('Error message:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  deleteTeamGroup: async (groupId, enqueueSnackbar) => {
    try {
      const response = await apiClient.delete(`/v2/admin/team-groups/${groupId}`);
      clearCache('teamGroups');
      return response;
    } catch (error) {
      console.error('Error message:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  addTeamsToGroup: async (groupId, teamIds, enqueueSnackbar) => {
    try {
      const response = await apiClient.post(`/v2/admin/team-groups/${groupId}/teams`, {
        team_ids: teamIds
      });
      clearCache('teamGroups');
      return response;
    } catch (error) {
      console.error('Error message:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  removeTeamFromGroup: async (groupId, teamId, enqueueSnackbar) => {
    try {
      const response = await apiClient.delete(`/v2/admin/team-groups/${groupId}/teams/${teamId}`);
      clearCache('teamGroups');
      return response;
    } catch (error) {
      console.error('Error message:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  // User-Team Management (new methods for database-based assignments)
  getUserTeams: async (userId, enqueueSnackbar) => {
    try {
      const response = await apiClient.get(`/v2/admin/users/${userId}/teams`);
      return response;
    } catch (error) {
      console.error('Error getting user teams:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  assignUserToTeams: async (userId, teamIds, role = 'member', enqueueSnackbar) => {
    try {
      const response = await apiClient.post(`/v2/admin/users/${userId}/teams`, {
        team_ids: teamIds,
        role
      });
      clearCache('users');
      clearCache('teams');
      return response;
    } catch (error) {
      console.error('Error assigning user to teams:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  // City Explorer location assignment (per-user allowlist on top of the
  // `city_explorer` product claim - see backend/app/blueprints/user_management_routes.py)
  listCityExplorerLocations: async (enqueueSnackbar) => {
    try {
      return await apiClient.get('/v2/admin/city-explorer-locations');
    } catch (error) {
      console.error('Error listing City Explorer locations:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  getUserCityExplorerLocations: async (userId, enqueueSnackbar) => {
    try {
      return await apiClient.get(`/v2/admin/users/${userId}/city-explorer-locations`);
    } catch (error) {
      console.error('Error getting user City Explorer locations:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  assignUserToCityExplorerLocations: async (userId, locationIds, enqueueSnackbar) => {
    try {
      const response = await apiClient.post(`/v2/admin/users/${userId}/city-explorer-locations`, {
        location_ids: locationIds,
      });
      clearCache('users');
      return response;
    } catch (error) {
      console.error('Error assigning user to City Explorer locations:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  removeUserFromTeam: async (userId, teamId, enqueueSnackbar) => {
    try {
      const response = await apiClient.delete(`/v2/admin/users/${userId}/teams/${teamId}`);
      clearCache('users');
      clearCache('teams');
      return response;
    } catch (error) {
      console.error('Error removing user from team:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  // User-TeamGroup Management (new methods for database-based assignments)
  getUserTeamGroups: async (userId, enqueueSnackbar) => {
    try {
      const response = await apiClient.get(`/v2/admin/users/${userId}/teamgroups`);
      return response;
    } catch (error) {
      console.error('Error getting user teamgroups:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  assignUserToTeamGroups: async (userId, teamGroupIds, role = 'member', enqueueSnackbar) => {
    try {
      const response = await apiClient.post(`/v2/admin/users/${userId}/teamgroups`, {
        teamgroup_ids: teamGroupIds,
        role
      });
      clearCache('users');
      clearCache('teamGroups');
      return response;
    } catch (error) {
      console.error('Error assigning user to teamgroups:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  removeUserFromTeamGroup: async (userId, teamGroupId, enqueueSnackbar) => {
    try {
      const response = await apiClient.delete(`/v2/admin/users/${userId}/teamgroups/${teamGroupId}`);
      clearCache('users');
      clearCache('teamGroups');
      return response;
    } catch (error) {
      console.error('Error removing user from teamgroup:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  // Team Search
  searchTeams: async (query, enqueueSnackbar) => {
    try {
      const response = await apiClient.get('/v2/admin/teams/search', {
        params: { q: query }
      });
      return response;
    } catch (error) {
      console.error('Error searching teams:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  // Get Team Schools
  getTeamSchools: async (teamId, enqueueSnackbar) => {
    try {
      const response = await apiClient.get(`/v2/admin/teams/${teamId}/schools`);
      return response;
    } catch (error) {
      console.error('Error getting team schools:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  // Schools Data Validation
  getSchoolsValidationData: async (params = {}) => {
    // Create a stable cache key that includes all filters
    const cacheParams = {
      page: params.page || 0,
      pageSize: params.pageSize || 50,
      search: params.search?.trim() || '',
      state: params.state?.trim() || '', 
      team: params.team?.trim() || '',
      schoolType: params.schoolType === 'all' ? '' : (params.schoolType?.trim() || ''),
      minCompleteness: params.minCompleteness
    };
    
    // Remove empty values for cleaner cache key
    const cleanParams = {};
    Object.keys(cacheParams).forEach(key => {
      if (cacheParams[key] !== '' && cacheParams[key] !== undefined && cacheParams[key] !== null) {
        cleanParams[key] = cacheParams[key];
      }
    });
    
    // Don't use cache - fetch fresh data every time for now to debug
    try {
      let response = await apiClient.get('/v2/admin/schools', {
        params: cleanParams
      });
      
      // Check if response is a string and parse it
      if (typeof response === 'string') {
        try {
          response = JSON.parse(response);
        } catch (parseError) {
          console.error('Failed to parse JSON response:', parseError);
          throw new Error('Invalid JSON response from server');
        }
      }
      
      if (!response) {
        throw new Error('No response from server');
      }
      
      if (!response.pagination) {
        console.error('Response missing pagination:', response);
        throw new Error('Invalid response structure - missing pagination');
      }
      
      return response;
    } catch (error) {
      console.error('Error fetching schools validation data:', error);
      // Clear any stale cache on error
      clearCache('schools-validation');
      throw error;
    }
  },

  getDistrictMetrics: async (params, enqueueSnackbar) => {
    try {
      return await schoolsApi.getDistrictMetrics(enqueueSnackbar);
    } catch (error) {
      console.error('Error fetching district metrics:', error);
      handleApiError(error, enqueueSnackbar);
      throw error;
    }
  },

  // Contact Form
  submitContact: async (formData, enqueueSnackbar) => {
    try {
      return await apiClient.post('/admin/contact', formData);
    } catch (error) {
      console.error('Error message:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  // Authentication
  resetPassword: async (oobCode, newPassword, enqueueSnackbar) => {
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      
      const response = await fetch(`${API_URL}/v2/auth/password-created`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ oobCode }),
      });
  
      if (!response.ok) {
        console.warn('Password creation tracking failed, but password was reset');
      }
  
      return true;
    } catch (error) {
      console.error('Password reset error:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  // School Management and Deletion
  softDeleteSchool: async (schoolUuid, enqueueSnackbar) => {
    try {
      const response = await apiClient.patch(`/v2/school/${schoolUuid}/soft-delete`);
      clearCache('schools');
      clearCache('schools-validation');
      return response;
    } catch (error) {
      console.error('Soft delete error:', error);
      if (error.status === 403) {
        error.message = error.response?.data?.error || 'Admin privileges required or school cannot be deleted';
      }
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  hardDeleteSchool: async (schoolUuid, force = false, enqueueSnackbar) => {
    try {
      const response = await apiClient.delete(`/v2/school/${schoolUuid}/hard-delete`, {
        data: { force }
      });
      clearCache('schools');
      clearCache('schools-validation');
      return response;
    } catch (error) {
      console.error('Hard delete error:', error);
      if (error.status === 403) {
        error.message = error.response?.data?.error || 'Admin privileges required or school cannot be deleted';
      }
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  reactivateSchool: async (schoolUuid, enqueueSnackbar) => {
    try {
      const response = await apiClient.patch(`/v2/school/${schoolUuid}/reactivate`);
      clearCache('schools');
      clearCache('schools-validation');
      return response;
    } catch (error) {
      console.error('Reactivate error:', error);
      if (error.status === 403) {
        error.message = 'Admin privileges required';
      }
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  // Academic Data Coverage
  getAcademicDataCoverage: async (enqueueSnackbar) => {
    try {
      const response = await apiClient.get('/v2/admin/academic-data-coverage');
      return response;
    } catch (error) {
      console.error('Error getting academic data coverage:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  generateActivationLinks: async (uids, enqueueSnackbar) => {
    try {
      const response = await apiClient.post('/v2/admin/users/generate-activation-links', { uids });
      return response;
    } catch (error) {
      console.error('Generate activation links error:', error);
      if (enqueueSnackbar) {
        handleApiError(error, enqueueSnackbar);
      }
      throw error;
    }
  },

  clearCache,
};

export { adminService };
export const clearAdminCache = clearCache;